
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from './Icons';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** If true, prevent body scrolling while modal is open. */
  disableBodyScroll?: boolean;
  /** Optional size: 'sm' | 'md' | 'lg' | 'xl' | 'full' */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'full';
  /** Hide the header area (title + built-in close button) */
  hideHeader?: boolean;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, disableBodyScroll = false, size = 'md', hideHeader = false }) => {
  useEffect(() => {
    if (!isOpen) return;
    const prevBody = document.body.style.overflow;
    if (disableBodyScroll) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevBody; };
  }, [isOpen, disableBodyScroll]);

  if (!isOpen) return null;
  const sizeClass = size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-4xl' : size === 'xl' ? 'max-w-7xl' : size === 'wide' ? 'max-w-[66vw]' : size === 'full' ? 'max-w-full' : 'max-w-lg';

  const modal = (
    <div className="fixed inset-0 z-[11000] flex items-start justify-center p-4 pt-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose}></div>
      <div className={`relative w-full ${sizeClass} bg-night-900 border border-white/10 rounded-2xl shadow-2xl ${size === 'full' ? 'p-0 h-screen rounded-none' : 'p-6'} animate-fade-up ${size === 'full' ? 'max-h-full' : (size === 'wide' ? 'max-h-[98vh]' : 'max-h-[90vh]')} overflow-y-auto`} style={{ pointerEvents: 'auto', height: size === 'wide' ? 'calc(100vh - 80px)' : undefined }}>
        {!hideHeader && (
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
